import { useState } from 'react';
import { Filter } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Badge } from '@/shared/components/ui/Badge';
import { Input } from '@/shared/components/ui/Input';
import { BarChart } from '@/shared/components/Charts/BarChart';
import { useFunnel } from '../hooks/useAnalytics';
import { PanelState } from './PanelState';
import { statusLabel, formatCount, defaultDateRange } from '../constants';

export function FunnelPanel({ enabled }: { enabled: boolean }) {
  const [range, setRange] = useState(defaultDateRange);

  const { data, isLoading, isError } = useFunnel({
    from: range.from,
    to: range.to,
    enabled,
  });

  // Ordered progression bars (rejected is intentionally shown separately below).
  const chartData = (data?.stages ?? []).map((s) => ({
    label: statusLabel(s.status),
    count: s.count,
  }));

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            <Filter className="h-4 w-4" /> Lead Conversion Funnel
          </span>
        }
        subtitle="New → Contacted → Interested → Converted. Rejected is a separate exit."
        action={
          <div className="flex items-center gap-2">
            <div className="w-36">
              <Input
                name="funnel-from"
                type="date"
                value={range.from}
                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <span className="text-on-surface-variant">–</span>
            <div className="w-36">
              <Input
                name="funnel-to"
                type="date"
                value={range.to}
                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
          </div>
        }
      />
      <CardBody className="space-y-4">
        <PanelState
          isLoading={isLoading}
          isError={isError}
          isEmpty={!!data?.empty}
          emptyTitle="No leads in this range"
          emptyDescription="No leads were created in the selected window for the current scope."
          emptyIcon={<Filter className="h-6 w-6" />}
        >
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Kpi label="Total leads" value={formatCount(data?.total ?? 0)} />
            <Kpi label="Converted" value={formatCount(data?.converted ?? 0)} />
            <Kpi label="Rejected" value={formatCount(data?.rejected ?? 0)} />
            <Kpi label="Conversion rate" value={`${(data?.conversionRate ?? 0).toFixed(1)}%`} />
          </div>

          <BarChart data={chartData} xKey="label" yKey="count" color="#6E62FF" height={260} />

          <div className="flex flex-wrap gap-2">
            {(data?.stages ?? []).map((s) => (
              <Badge key={s.status} tone="info">
                {statusLabel(s.status)}: {formatCount(s.count)}
              </Badge>
            ))}
            <Badge tone="danger">Rejected: {formatCount(data?.rejected ?? 0)}</Badge>
          </div>
        </PanelState>
      </CardBody>
    </Card>
  );
}

function Kpi({ label, value }: { label: string; value: number | string }) {
  return (
    <Card className="p-4">
      <p className="text-display-sm font-semibold text-on-surface">{value}</p>
      <p className="mt-1 text-body-sm text-on-surface-variant">{label}</p>
    </Card>
  );
}
